// Text.tsx
import React from 'react';

type Props<T extends React.ElementType = 'span'> = {
  tag?: T;
  className?: string;
  children: string;
  isBold?: boolean;
  isFormattedText?: boolean;
} & Omit<React.ComponentPropsWithoutRef<T>, 'children' | 'className'>;

const tokenize = (str: string, isBold?: boolean): React.ReactNode[] => str
  .split(/(\*\*.+?\*\*|\[[^\]]+\]\([^)]+\))/g)
  .filter(Boolean)
  .map((chunk) => {
    // use kalnia font for isBold text unless isBold prop is true
    if (/^\*\*.+\*\*$/.test(chunk)) {
      return (
        <span
          key={chunk.slice(2, -2)}
          className={isBold ? 'isBold' : 'font-kalnia'}
        >
          {chunk.slice(2, -2)}
        </span>
      );
    }

    const linkMatch = chunk.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={linkMatch[2]}
          href={linkMatch[2]}
          target='_blank'
          rel='noopener noreferrer'
          className='underline'
        >
          {linkMatch[1]}
        </a>
      );
    }
    return chunk;
  });

function Text<T extends React.ElementType = 'span'>({
  tag,
  className,
  children,
  isBold,
  isFormattedText,
  ...rest
}: Props<T>) {
  const Component = tag || 'span';
  if (isFormattedText) {
    return (
      <Component
        {...rest}
        className={className}
        dangerouslySetInnerHTML={{
          __html: children.replace(/\/n/g, '<br/>'),
        }}
      />
    );
  }
  return (
    <Component
      {...rest}
      className={className}
      style={{ whiteSpace: 'pre-wrap', ...(rest as React.HTMLAttributes<HTMLElement>).style }}
    >
      {tokenize(children, isBold)}
    </Component>
  );
}

export default Text;
